import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { ArrowDown } from "lucide-react";
import { DotGrid, EqualMark, BarsMark } from "@/components/site/Marks";
import AtmosphereParticles from "@/components/site/AtmosphereParticles";

const Hero = () => {
  const { t } = useLang();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* ambient horizon glow */}
      <div
        className="absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 120%, rgba(217,121,37,0.22) 0%, rgba(176,36,24,0.10) 30%, rgba(5,5,5,0) 60%)",
        }}
      />
      <div
        className="absolute inset-0 z-0 opacity-[0.12]"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1521194263619-39ecc5b55c61?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          mixBlendMode: "luminosity",
        }}
      />

      {/* atmospheric particle field — reacts to the mouse */}
      <AtmosphereParticles className="absolute inset-0 z-[1]" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 w-full pt-28 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-10"
          data-testid="hero-name-block"
        >
          <span className="block h-px w-20 bg-[#d97925] mb-4" aria-hidden="true" />
          <h2
            data-testid="hero-name"
            className="font-serif-display italic font-light text-[#d97925] leading-none tracking-tight text-3xl md:text-4xl lg:text-5xl"
          >
            Marián&nbsp;Hriňa
          </h2>
        </motion.div>

        <h1 className="leading-[0.95] tracking-tight">
          {[t("hero.title1"), t("hero.title2"), t("hero.title3")].map((line, i) => {
            let cls, color;
            if (i === 0) {
              cls = "font-tech uppercase tracking-[0.06em] text-[13vw] md:text-[8.4vw] lg:text-[7vw] leading-none";
              color = "#d97925";
            } else if (i === 1) {
              cls = "font-poster uppercase text-[9.5vw] md:text-[6.4vw] lg:text-[5.3vw]";
              color = "#b02418";
            } else {
              cls = "font-serif-display italic font-light text-[10vw] md:text-[6.6vw] lg:text-[5.6vw]";
              color = "#f5f2eb";
            }
            return (
              <motion.span
                key={`${line}-${i}`}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                className={`block ${cls}`}
                style={{ color }}
              >
                {line}
              </motion.span>
            );
          })}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-10 max-w-xl text-[#a3a09a] text-base md:text-lg leading-relaxed font-light"
          data-testid="hero-sub"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-cta-gallery"
            onClick={() => scrollTo("gallery")}
            className="bg-[#f5f2eb] text-[#050505] px-8 py-4 overline rounded-full hover:bg-white transition-colors"
          >
            {t("hero.cta1")}
          </button>
          <button
            data-testid="hero-cta-contact"
            onClick={() => scrollTo("contact")}
            className="border border-white/20 text-[#f5f2eb] px-8 py-4 overline rounded-full hover:border-[#b02418] hover:text-[#d97925] transition-colors"
          >
            {t("hero.cta2")}
          </button>
        </motion.div>
      </div>

      <EqualMark className="absolute right-[9%] top-[20%] opacity-60 hidden lg:block" color="#d97925" w={54} />
      <DotGrid className="absolute right-[7%] bottom-[20%] opacity-35 hidden lg:block" color="#d97925" cols={6} rows={6} gap={11} />
      <BarsMark className="absolute left-[3.5%] top-[42%] opacity-25 hidden lg:block" color="#f5f2eb" count={4} w={32} />

      <motion.button
        onClick={() => scrollTo("gallery")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{ opacity: { delay: 1.2 }, y: { repeat: Infinity, duration: 2.4 } }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[#8d8a83] hover:text-[#f5f2eb]"
        aria-label="scroll down"
      >
        <ArrowDown size={22} />
      </motion.button>
    </section>
  );
};

export default Hero;
